// ═══════════════════════════════════════════════════════════════
// Intake — Module Layout with sub-navigation
// ═══════════════════════════════════════════════════════════════

import { Outlet, useLocation } from 'react-router-dom';
import { Group, Button, Tooltip, CopyButton, ActionIcon } from '../../components/gab/GabComponents';
import {
  IconLayoutDashboard, IconList, IconUserPlus, IconLogout,
  IconBox, IconChartBar, IconWorld, IconCheck, IconCopy,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import type { NavItem } from '../../types/nav';
import { brand } from '../../lib/colors';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ModuleNav from '../../components/layout/ModuleNav';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/intake', icon: IconLayoutDashboard, exact: true },
  { label: 'Register', path: '/intake/register', icon: IconUserPlus },
  { label: 'Roster', path: '/intake/roster', icon: IconList },
  { label: 'Discharge', path: '/intake/discharge', icon: IconLogout },
  { label: 'Property', path: '/intake/property', icon: IconBox },
  { label: 'Reports', path: '/intake/reports', icon: IconChartBar, minLevel: 'editor' },
];

export default function IntakeLayout() {
  const { pathname } = useLocation();
  const { activeIncident } = useAuthStore();
  const visibleItems = useFilteredNav('intake', NAV_ITEMS);

  // Hide sub-nav on individual detail pages (they have their own breadcrumbs)
  const isDetail = pathname.startsWith('/intake/individual/');

  const portalUrl = activeIncident
    ? `${window.location.origin}/portal/${activeIncident.id}`
    : '';

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  const portalExtra = activeIncident ? (
    <Group gap={4}>
      <Tooltip label="Open public family lookup portal" withArrow>
        <Button
          variant="light"
          color="orange"
          size="xs"
          leftSection={<IconWorld size={14} />}
          component="a"
          href={portalUrl}
          target="_blank"
        >
          Family Portal
        </Button>
      </Tooltip>
      <CopyButton value={portalUrl}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied!' : 'Copy link'} withArrow>
            <ActionIcon variant="light" color={copied ? 'green' : 'orange'} size="sm" onClick={copy}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  ) : undefined;

  return (
    <div style={{ padding: '24px' }}>
      {!isDetail && (
        <>
          <Breadcrumbs
            items={[
              { label: 'Hub', to: '/' },
              { label: 'Intake', to: '/intake' },
              ...(currentPage && currentPage.path !== '/intake' ? [{ label: currentPage.label }] : []),
            ]}
          />
          <ModuleNav items={visibleItems} accentColor={brand.blurple} extra={portalExtra} />
        </>
      )}
      <Outlet />
    </div>
  );
}
